import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  fileUploadErrorSelector,
  fileUploadProgressSelector,
  uploadedFileSelector
} from '../../modules/selectors/files'
import { clearUploadedFile, updateFileUploadProgress } from '../../modules/actions/files'

export default function useFileUpload({ fileId, fileName, fileUrl, onFileRemove, onFileUploaded }) {
  const dispatch = useDispatch()

  const [selectedFile, setSelectedFile] = useState({})
  const [fileURLBlob, setFileURLBlob] = useState('')
  // when file successfully uploaded set the internal state, that is done to make one time callback on successful
  // FileUploaded to avoid infinity loops based on onFileUploaded func change due to dependencies
  const [fileWasUploaded, setFileWasUploaded] = useState(false)
  const [allowFileDelete, setAllowFileDelete] = useState(!!fileUrl)

  const uploadingFileKey = fileId || fileName || selectedFile.name

  const fileUploadError = useSelector(fileUploadErrorSelector)
  const { [uploadingFileKey]: uploadedFileData } = useSelector(uploadedFileSelector)
  const { [uploadingFileKey]: fileUploadProgress } = useSelector(fileUploadProgressSelector)

  const fileIsUploaded = useMemo(() => fileUploadProgress === 100, [fileUploadProgress])
  const isFileLoading = useMemo(() => !!fileUploadProgress && !fileIsUploaded, [fileUploadProgress, fileIsUploaded])

  const handleRemoveFile = useCallback(() => {
    setSelectedFile({})
    setFileURLBlob('')
    setAllowFileDelete(false)
    dispatch(updateFileUploadProgress({ [uploadingFileKey]: 0 }))
    dispatch(clearUploadedFile(uploadingFileKey))
    // callback for parent components
    onFileRemove && onFileRemove()
  }, [dispatch, onFileRemove, uploadingFileKey])

  useEffect(() => {
    if (fileIsUploaded) {
      setFileWasUploaded(true)
    }
  }, [fileIsUploaded, setFileWasUploaded])

  useEffect(() => {
    // when file successfully uploaded make a callback to Parent component
    if (fileWasUploaded) {
      onFileUploaded && onFileUploaded(uploadedFileData)
      setFileWasUploaded(false)
      setAllowFileDelete(true)
    }
  }, [fileWasUploaded, uploadedFileData, onFileUploaded])

  useEffect(() => {
    if (fileUploadError) {
      // remove file when the uploading failed
      handleRemoveFile()
    }
  }, [handleRemoveFile, fileUploadError])

  return {
    selectedFile,
    setSelectedFile,
    fileURLBlob,
    setFileURLBlob,
    uploadedFileData,
    isFileLoading,
    fileUploadProgress,
    fileIsUploaded,
    handleRemoveFile,
    allowFileDelete
  }
}
